import React, { useEffect, useRef } from "react";
import { X, Search, Clock, UtensilsCrossed, ChevronLeft } from "lucide-react";
import Button from "../../../../common/Button/Button";
import OuterModalListItem from "../../../../common/OuterModalListItem/OuterModalListItem";
import CompactNutrientSummaryCard from "../nutrition/CompactNutritionCard";
import { getMealFoodId, getRecipeCatalogId, getRecipeName } from "../../core/diet-plan/recipeId";
import { RECIPE_SEARCH_MIN } from "../../core/recipes/recipeCatalog";
import IntakeTimePicker from "./IntakeTimePicker";

function StreamlinedFoodAddModal({
  isOpen,
  onClose,
  onAddFood,
  searchInputValue,
  setSearchInputValue,
  searchResults,
  searchLoading,
  searchTotalCount,
  foodState,
  setFoodState,
  onFoodSelect,
  calculatedValues,
  availableQuantityTypes = [],
  // When the meal slot owns the intake time, new recipes inherit it instead.
  hideIntakeTime = false,
}) {
  const resolveFoodOrCatalogId = (item) =>
    getMealFoodId(item) || getRecipeCatalogId(item);

  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 80);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === "Escape") {
        onClose();
    } else if (e.key === "Enter" && resolveFoodOrCatalogId(foodState.activeFoodItem)) {
        if (foodState.gramsInputValue && parseFloat(foodState.gramsInputValue) > 0) {
          onAddFood();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, foodState, onAddFood, onClose]);

  if (!isOpen) return null;

  const handleFoodSelect = async (foodItem) => {
    if (onFoodSelect) {
      await onFoodSelect(foodItem);
    } else {
      const conversions = foodItem.quantity_conversions?.conversions || {};
      const availableUnits = Object.keys(conversions);
      const defaultUnit = availableUnits[0] || "g";
      setFoodState({
        activeFoodItem: {
          ...foodItem,
          common_name: foodItem.common_name || "",
          food_id: resolveFoodOrCatalogId(foodItem),
          recipe_id: resolveFoodOrCatalogId(foodItem),
        },
        gramsInputValue: "1",
        timeValue: "",
        quantityType: defaultUnit,
        availableQuantityTypes: availableUnits.length > 0 ? availableUnits : [defaultUnit],
      });
    }
  };

  const handleBackToSearch = () => {
    setFoodState({
      activeFoodItem: {},
      gramsInputValue: "",
      timeValue: "",
      quantityType: "",
      availableQuantityTypes: [],
    });
  };

  const hasSelection = !!resolveFoodOrCatalogId(foodState.activeFoodItem);
  const totalsForCard = {
    calories: parseFloat(calculatedValues?.calories) || 0,
    proteins: parseFloat(calculatedValues?.protein) || 0,
    carbs: parseFloat(calculatedValues?.carbs) || 0,
    fats: parseFloat(calculatedValues?.fats) || 0,
    fiber: parseFloat(calculatedValues?.fiber) || 0,
    sugars: parseFloat(calculatedValues?.sugar ?? calculatedValues?.sugars) || 0,
  };
  const canAdd = hasSelection && foodState.gramsInputValue && parseFloat(foodState.gramsInputValue) > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        data-testid="food-add-modal"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between shrink-0 px-5 py-4 border-b border-gray-100 bg-gray-50/80">
          {hasSelection ? (
            <button
              type="button"
              onClick={handleBackToSearch}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Change food
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-orange-100">
                <UtensilsCrossed className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Add food</h2>
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search bar - always visible */}
        <div className="shrink-0 px-5 py-4 border-b border-gray-100 bg-white">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchInputValue}
              onChange={(e) => setSearchInputValue(e.target.value)}
              className="w-full pl-10 pr-4 py-3 text-base border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-0 focus:border-orange-primary transition-all placeholder:text-gray-400"
              placeholder="Search foods (e.g. rice, egg, chicken)"
            />
            {searchInputValue.trim().length > 0 && searchInputValue.trim().length < RECIPE_SEARCH_MIN && (
              <p className="mt-1.5 text-xs text-gray-400">Type at least {RECIPE_SEARCH_MIN} characters</p>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {hasSelection ? (
            <div className="p-5 space-y-5">
              {/* Selected food */}
              <div className="rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100 p-4">
                <p className="text-sm font-medium text-orange-800/80">Selected</p>
                <h3 className="text-base font-semibold text-gray-900 mt-0.5">
                  {foodState.activeFoodItem.food_name}
                  {foodState.activeFoodItem.common_name && (
                    <span className="font-normal text-gray-600"> · {foodState.activeFoodItem.common_name}</span>
                  )}
                </h3>
              </div>

              {/* Quantity + unit row */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity & unit</label>
                <div className="flex gap-3">
                  <input
                    type="number"
                    inputMode="decimal"
                    value={foodState.gramsInputValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        setFoodState((prev) => ({ ...prev, gramsInputValue: value }));
                      }
                    }}
                    onBlur={() => {
                      setFoodState((prev) => ({
                        ...prev,
                        gramsInputValue: prev.gramsInputValue
                          ? parseFloat(prev.gramsInputValue).toFixed(2)
                          : "",
                      }));
                    }}
                    className="flex-1 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:ring-0 focus:border-orange-primary"
                    placeholder="e.g. 100"
                  />
                  <select
                    value={foodState.quantityType}
                    onChange={(e) =>
                      setFoodState((prev) => ({ ...prev, quantityType: e.target.value }))
                    }
                    className="px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:ring-0 focus:border-orange-primary min-w-[7rem]"
                  >
                    {availableQuantityTypes.map((unitKey) => {
                      const unitData =
                        foodState.activeFoodItem?.quantity_conversions?.conversions?.[unitKey];
                      const value = unitData?.value || 1;
                      const unitLabel = unitData?.unit || "g";
                      return (
                        <option key={unitKey} value={unitKey}>
                          {unitKey} ({value} {unitLabel})
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {/* Intake time (optional) */}
              {!hideIntakeTime && (
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-600 mb-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  Intake time (optional)
                </label>
                <IntakeTimePicker
                  value={foodState.timeValue}
                  onChange={(timeValue) =>
                    setFoodState((prev) => ({ ...prev, timeValue }))
                  }
                  className="w-full"
                />
              </div>
              )}

              {/* Nutrition - same card as template/plan pages */}
              <CompactNutrientSummaryCard totals={totalsForCard} label="For this amount" />

              {/* Primary CTA */}
              <div className="pt-2">
                <Button
                  onClick={onAddFood}
                  text="Add to meal"
                  data-testid="food-add-to-meal"
                  className="w-full bg-orange-primary text-white py-3.5 rounded-xl font-semibold text-base hover:bg-orange-600 transition-colors shadow-lg shadow-orange-500/20"
                  disabled={!canAdd}
                />
              </div>
            </div>
          ) : (
            <div className="p-5">
              {searchLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-10 h-10 border-2 border-orange-primary border-t-transparent rounded-full animate-spin" />
                  <p className="mt-4 text-gray-500 text-sm">Searching foods…</p>
                </div>
              ) : searchResults?.length > 0 ? (
                <div>
                  <p className="text-sm text-gray-500 mb-3">
                    {searchTotalCount ?? searchResults.length} result
                    {(searchTotalCount ?? searchResults.length) !== 1 ? "s" : ""}
                  </p>
                  <ul className="space-y-2">
                    {searchResults.map((foodItem) => (
                      <li key={resolveFoodOrCatalogId(foodItem)}>
                        <OuterModalListItem
                          name={getRecipeName(foodItem)}
                          subtitle={foodItem.common_name || foodItem.common_names || ""}
                          clickHandler={() => handleFoodSelect(foodItem)}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ) : searchInputValue.trim().length >= RECIPE_SEARCH_MIN ? (
                <div className="text-center py-12">
                  <Search className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No foods found</p>
                  <p className="text-sm text-gray-400 mt-1">Try a different search (e.g. brand, ingredient)</p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="inline-flex p-4 rounded-2xl bg-gray-100 mb-4">
                    <UtensilsCrossed className="w-10 h-10 text-gray-300" />
                  </div>
                  <p className="text-gray-500 font-medium">Search to add a food</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Type at least {RECIPE_SEARCH_MIN} characters above
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StreamlinedFoodAddModal;
